"use client";

import { useCallback, useMemo, useState } from "react";

/* -- Public Types -- */

export interface SliderEntry {
  key: string;
  label: string;
  /** Tailwind class for dot/bar segment, e.g. "bg-blue-500" */
  color: string;
  /** CSS hex color for the slider gradient, e.g. "#3b82f6" */
  barColor: string;
}

export interface WeightedSliderEditorProps {
  /** Entries to display as slider rows (order is preserved) */
  entries: SliderEntry[];
  /** Current weight values (0-1 fractions, should sum to ~1.0) */
  weights: Record<string, number>;
  /** Called with the full rebalanced weight map when any slider changes */
  onWeightsChange: (weights: Record<string, number>) => void;
  /** If provided, each row gets a remove button */
  onEntryRemove?: (key: string) => void;
  /** Entries that can be added (renders "+ label" buttons below sliders) */
  addableEntries?: Array<{ key: string; label: string }>;
  /** Called when an addable entry is selected */
  onEntryAdd?: (key: string) => void;
  /** If provided, renders a Select All / Deselect All toggle */
  selectAllLabel?: string;
  onToggleAll?: () => void;
  /** Hint text shown below the stacked bar */
  hint?: string;
}

/* -- Rebalance Logic -- */

/**
 * Rebalance weights so they sum to 1.0.
 * When `changedKey` is adjusted to `newFraction`, the remaining unlocked keys
 * are proportionally scaled to absorb the difference.
 */
function rebalanceMix(
  current: Record<string, number>,
  activeKeys: string[],
  changedKey: string,
  newFraction: number,
  lockedKeys: Set<string>,
): Record<string, number> {
  const clamped = Math.max(0, Math.min(1, newFraction));
  const result = { ...current, [changedKey]: clamped };

  const fixedKeys = new Set([...lockedKeys, changedKey]);
  const fixedSum = activeKeys
    .filter((k) => fixedKeys.has(k))
    .reduce((s, k) => s + (result[k] ?? 0), 0);

  const flexibleKeys = activeKeys.filter((k) => !fixedKeys.has(k));
  const remaining = Math.max(0, 1 - fixedSum);
  const flexibleSum = flexibleKeys.reduce((s, k) => s + (current[k] ?? 0), 0);

  if (flexibleKeys.length === 0) return result;

  if (flexibleSum > 0) {
    const scale = remaining / flexibleSum;
    for (const k of flexibleKeys) {
      result[k] = Math.max(0, (current[k] ?? 0) * scale);
    }
  } else {
    const each = remaining / flexibleKeys.length;
    for (const k of flexibleKeys) {
      result[k] = each;
    }
  }

  // Fix rounding drift by nudging the largest flexible key
  const total = activeKeys.reduce((s, k) => s + (result[k] ?? 0), 0);
  const drift = total - 1;
  if (Math.abs(drift) > 0.0001 && flexibleKeys.length > 0) {
    const largest = flexibleKeys.reduce((a, b) =>
      (result[a] ?? 0) >= (result[b] ?? 0) ? a : b,
    );
    result[largest] = Math.max(0, (result[largest] ?? 0) - drift);
  }

  return result;
}

/* -- Component -- */

export function WeightedSliderEditor({
  entries,
  weights,
  onWeightsChange,
  onEntryRemove,
  addableEntries,
  onEntryAdd,
  selectAllLabel,
  onToggleAll,
  hint,
}: WeightedSliderEditorProps) {
  const [lockedKeys, setLockedKeys] = useState<Set<string>>(new Set());

  const activeKeys = useMemo(() => entries.map((e) => e.key), [entries]);

  const totalPct = useMemo(
    () => activeKeys.reduce((sum, k) => sum + (weights[k] ?? 0), 0),
    [activeKeys, weights],
  );

  const isBalanced = Math.abs(totalPct - 1.0) < 0.02;

  const toggleLock = useCallback((key: string) => {
    setLockedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleSliderChange = useCallback(
    (key: string, rawPct: number) => {
      const newFraction = rawPct / 100;
      const rebalanced = rebalanceMix(
        weights,
        activeKeys,
        key,
        newFraction,
        lockedKeys,
      );
      onWeightsChange(rebalanced);
    },
    [weights, activeKeys, lockedKeys, onWeightsChange],
  );

  const handleRemove = useCallback(
    (key: string) => {
      // Clear lock state for the removed key
      setLockedKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      onEntryRemove?.(key);
    },
    [onEntryRemove],
  );

  if (
    entries.length === 0 &&
    !onToggleAll &&
    (!addableEntries || addableEntries.length === 0)
  ) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Optional header row with toggle-all */}
      {onToggleAll && selectAllLabel && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Select entries and adjust their share.
          </p>
          <button
            type="button"
            onClick={onToggleAll}
            className="text-xs text-info-foreground hover:text-foreground font-medium px-2 py-1 rounded border border-info-border hover:bg-info shrink-0 ml-3"
          >
            {selectAllLabel}
          </button>
        </div>
      )}

      {entries.length > 0 && (
        <>
          {/* Stacked Bar */}
          <div className="space-y-1">
            <div className="flex h-7 rounded-lg overflow-hidden bg-surface-alt">
              {entries.map(({ key, color, label }) => {
                const pct = (weights[key] ?? 0) * 100;
                if (pct <= 0) return null;
                return (
                  <div
                    key={key}
                    className={`${color} transition-all duration-300 flex items-center justify-center`}
                    style={{ width: `${pct}%` }}
                    title={`${label}: ${pct.toFixed(1)}%`}
                  >
                    {pct >= 8 && (
                      <span className="text-[10px] font-medium text-white truncate px-1">
                        {pct.toFixed(0)}%
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between">
              <p className="text-[11px] text-dim-foreground">
                {hint ??
                  "Drag a slider to adjust \u2014 others rebalance automatically. Click the lock to freeze a value."}
              </p>
              <span
                className={`text-xs font-medium ${isBalanced ? "text-success-muted" : "text-danger-foreground"}`}
              >
                Total: {(totalPct * 100).toFixed(1)}%
              </span>
            </div>
          </div>

          {/* Slider rows */}
          <div className="space-y-3">
            {entries.map(({ key, label, color, barColor }) => {
              const fraction = weights[key] ?? 0;
              const pct = fraction * 100;
              const isLocked = lockedKeys.has(key);

              return (
                <div key={key} className="group">
                  <div className="flex items-center gap-3">
                    {/* Optional remove button */}
                    {onEntryRemove && (
                      <button
                        type="button"
                        onClick={() => handleRemove(key)}
                        className="p-1 rounded text-dim-foreground hover:text-danger-foreground transition-colors shrink-0"
                        title={`Remove ${label}`}
                      >
                        <svg
                          role="img"
                          aria-hidden="true"
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}

                    {/* Lock toggle */}
                    <button
                      type="button"
                      onClick={() => toggleLock(key)}
                      className={`p-1 rounded transition-colors shrink-0 ${
                        isLocked
                          ? "text-warning-muted hover:text-warning-foreground"
                          : "text-dim-foreground hover:text-muted-foreground"
                      }`}
                      title={
                        isLocked
                          ? "Unlock \u2014 allow auto-adjustment"
                          : "Lock \u2014 keep this value fixed"
                      }
                    >
                      {isLocked ? (
                        <svg
                          role="img"
                          aria-hidden="true"
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg
                          role="img"
                          aria-hidden="true"
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />
                        </svg>
                      )}
                    </button>

                    {/* Color dot + label */}
                    <div className="w-36 shrink-0 flex items-center gap-1.5">
                      <span
                        className={`w-2.5 h-2.5 rounded-full ${color} shrink-0`}
                      />
                      <span className="text-sm font-medium text-secondary-foreground truncate">
                        {label}
                      </span>
                    </div>

                    {/* Slider */}
                    <div className="flex-1 relative">
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={0.5}
                        value={pct}
                        onChange={(e) =>
                          handleSliderChange(key, Number(e.target.value))
                        }
                        className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, ${barColor} 0%, ${barColor} ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)`,
                        }}
                      />
                    </div>

                    {/* Percentage display */}
                    <span className="w-14 text-right text-sm font-semibold text-foreground tabular-nums shrink-0">
                      {pct.toFixed(1)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sum indicator (shown when entries can be added/removed) */}
          {onEntryRemove && (
            <div className="flex justify-between items-center pt-1 border-t border-border">
              <span className="text-[10px] text-dim-foreground">
                {entries.length} entries
              </span>
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs font-mono ${
                    Math.abs(totalPct * 100 - 100) > 1
                      ? "text-warning-muted font-semibold"
                      : "text-muted-foreground"
                  }`}
                >
                  {(totalPct * 100).toFixed(1)}%
                </span>
                {Math.abs(totalPct * 100 - 100) > 1 && (
                  <span className="text-[10px] text-warning-muted">
                    (should be 100%)
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Addable entries */}
      {addableEntries && addableEntries.length > 0 && onEntryAdd && (
        <div className="grid grid-cols-3 gap-1.5 pt-1">
          {addableEntries.map((entry) => (
            <button
              key={entry.key}
              type="button"
              onClick={() => onEntryAdd(entry.key)}
              className="text-xs text-dim-foreground hover:text-secondary-foreground hover:bg-surface-alt px-2 py-1.5 rounded border border-dashed border-border hover:border-border text-left truncate transition-colors"
            >
              + {entry.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
